/**
 * Сериализатор Query Builder
 * Преобразует UI дерево в API структуру для отправки на бэкенд
 */

import {
  ZFilterCriterion,
  ZFilterGroup,
  ZFilterItem,
} from "../types/ZFilterApiTypes";
import { ZOperatorsMapping } from "../types/ZFilterMetadataTypes";
import {
  ZCriterionNode,
  ZGroupNode,
  ZQueryBuilderNode,
} from "../types/ZQueryBuilderTypes";
import {
  validationContexts,
  ZValidationContext,
} from "../types/ZValidationContext";
import { operatorRequiresValue } from "./filterUtils";

const serializeGroupNode = (
  node: ZGroupNode,
  operatorsMapping?: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): ZFilterGroup => {
  const items: ZFilterItem[] = node.items.reduce<ZFilterItem[]>(
    (acc, child) => {
      const serialized = serializeNode(child, operatorsMapping, context);
      if (serialized !== null) {
        acc.push(serialized);
      }
      return acc;
    },
    [],
  );

  return {
    op: node.op,
    items,
  };
};

const serializeCriterionNode = (
  node: ZCriterionNode,
  operatorsMapping?: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): ZFilterCriterion | null => {
  let requiresValue = true;
  if (node.operator && node.valueType && operatorsMapping) {
    requiresValue = operatorRequiresValue(
      node.operator,
      operatorsMapping[node.valueType],
    );
  }

  const shouldSkipByEmptyValue =
    node.value === null || node.value === undefined || node.value === "";
  const shouldOmitValueForTemplate =
    context === validationContexts.saveTemplate &&
    requiresValue &&
    shouldSkipByEmptyValue;

  if (
    !node.fieldMeta ||
    !node.valueType ||
    !node.operator ||
    (requiresValue && shouldSkipByEmptyValue && !shouldOmitValueForTemplate)
  ) {
    return null;
  }

  return {
    fieldMeta: node.fieldMeta,
    type: node.valueType,
    operator: node.operator,
    ...(shouldOmitValueForTemplate ? {} : { value: node.value }),
  };
};

/**
 * Преобразование UI узла в API структуру
 * @param node - узел UI дерева
 * @returns API структура или null если узел невалидный
 */
const serializeNode = (
  node: ZQueryBuilderNode,
  operatorsMapping?: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): ZFilterItem | null => {
  if (node.type === "group") {
    return serializeGroupNode(node, operatorsMapping, context);
  }

  return serializeCriterionNode(node, operatorsMapping, context);
};

/**
 * Сериализация корневой группы в FilterGroup
 * @param rootGroup - корневая группа UI дерева
 * @returns FilterGroup для API запроса
 */
export const serializeFilterTree = (
  rootGroup: ZGroupNode,
  operatorsMapping?: ZOperatorsMapping,
  context: ZValidationContext = validationContexts.apply,
): ZFilterGroup => serializeGroupNode(rootGroup, operatorsMapping, context);
